import { useGetAccountImpactsDpQuery } from "features/impacts/impactsSlice";

function FocusScoreClient({ clientId, formType }) {
  let clientid = clientId;
  let focusScore;

  const {
    data: impactDpByAccountData,
    isError: isImpactDpByAccountError,
    isSuccess: isImpactDpByAccountSuccess,
  } = useGetAccountImpactsDpQuery(clientid, {
    refetchOnMountOrArgChange: true,
  });

  const getScore = (difference) => {
    let score = 0;
    if (difference < 0.1) {
      score = 1;
    } else if (difference >= 0.1 && difference < 0.2) {
      score = 2;
    } else if (difference >= 0.2 && difference < 0.3) {
      score = 3;
    } else if (difference >= 0.3 && difference < 0.4) {
      score = 4;
    } else if (difference >= 0.4 && difference < 0.5) {
      score = 5;
    } else if (difference >= 0.5 && difference < 0.6) {
      score = 6;
    } else if (difference >= 0.6 && difference < 0.9) {
      score = 7;
    } else if (difference >= 0.9 && difference < 1.1) {
      score = 8;
    } else if (difference >= 1.1 && difference < 1.3) {
      score = 9;
    } else if (difference >= 1.3) {
      score = 10;
    }
    return score;
  };
  const getScoreStatus = (score) => {
    const roundedScore = Math.round(score * 10) / 10;
    if (roundedScore >= 9 && roundedScore <= 10) {
      return "Great Progress";
    } else if (roundedScore >= 7 && roundedScore < 9) {
      return "Good";
    } else if (roundedScore >= 5 && roundedScore < 7) {
      return "Fair";
    } else if (roundedScore >= 1 && roundedScore < 5) {
      return "Problematic";
    } else {
      return "Invalid Score";
    }
  };
  const currentDate = new Date();
  const currentMonth = currentDate.getMonth();
  const currentYear = currentDate.getFullYear();

  //New calculation for Focus score
  const monthNames = [
    "January",
    "February",
    "March",
    "April",
    "May",
    "June",
    "July",
    "August",
    "September",
    "October",
    "November",
    "December",
  ];
  const findDataForCurrentorPreviousMonth = (month, year, dpData) => {
    let findData = null;
    let findMonth = month;
    let findYear = year;

    while (!findData && findYear >= 0 && dpData.length !== 0) {
      let monthName = monthNames[findMonth];
      let currentYear = findYear;
      findData = dpData.find(
        (item) =>
          item.title === monthName && item.year === parseInt(currentYear),
      );
      if (!findData) {
        findMonth--;
        if (findMonth < 0) {
          findMonth = 11;
          findYear--;
        }
      }
    }
    return findData ? findData : null;
  };

  const findDataForCurrentorPreviousYear = (year, dpData) => {
    let findData = null;
    let findYear = year;

    while (!findData && findYear >= 0 && dpData.length !== 0) {
      let currentYear = findYear;

      findData = dpData.find((item) => item.title === currentYear.toString());
      if (!findData) {
        findYear--;
      }
    }
    return findData ? findData : null;
  };
  const findDataForCurrentorPreviousQuarter = (quarter, year, dpData) => {
    let findData = null;
    let findQuarter = quarter;
    let findYear = year;

    while (!findData && findYear >= 0 && dpData.length !== 0) {
      let findQ = findQuarter;
      let currentYear = findYear;
      findData = dpData.find(
        (item) =>
          item.title === `Q${findQ}` && item.year === parseInt(currentYear),
      );
      if (!findData) {
        findQuarter--;
        if (findQuarter < 1) {
          findQuarter = 4;
          findYear--;
        }
      }
    }
    return findData ? findData : null;
  };

  const scoreDetails = new Map();

  const goalFocusScore = new Map();

  const calculateScoreDataPoints = (data) => {
    // let currentAverage = 0;
    // let targetAverage = 0;

    data.forEach((i) => {
      let data_points = [];
      data_points = i.data_points;
      if (i.tracking_frequency === "Monthly") {
        let lastDp;
        let currentAverage = 0;
        let targetAverage = 0;
        if (data_points !== null) {
          lastDp = findDataForCurrentorPreviousMonth(
            currentMonth,
            currentYear,
            data_points,
          );
        }
        if (lastDp) {
          currentAverage = Number(lastDp.cumulative_value);
          targetAverage = Number(lastDp.cumulative_target);
        }
        let impactFocusScore = 0;
        if (i.target_direction === "Increase") {
          impactFocusScore = getScore(
            (currentAverage / targetAverage).toFixed(2),
          );
        }
        if (i.target_direction === "Decrease") {
          impactFocusScore = getScore(
            (targetAverage / currentAverage).toFixed(2),
          );
        }

        if (!scoreDetails.has(i.goal)) {
          scoreDetails.set(i.goal, new Map());
        }

        const innerScoreMap = scoreDetails.get(i.goal);
        innerScoreMap.set(i.id, {
          impact_score: impactFocusScore,
          goal_priority: i.goal_priority,
        });
      }
      //tracking freq is quarterly
      if (i.tracking_frequency === "Quarterly") {
        let lastDp;
        let currentAverage = 0;
        let targetAverage = 0;
        const convertMonthNameToNumber = (monthName) => {
          const months = [
            "January",
            "February",
            "March",
            "April",
            "May",
            "June",
            "July",
            "August",
            "September",
            "October",
            "November",
            "December",
          ];
          return months.indexOf(monthName) + 1;
        };
        let fiscalYearStart = convertMonthNameToNumber(i.fiscal_year_start);
        const getCurrentQuarter = (currentMonth, fiscalYearStart) => {
          const fiscalMonth = (currentMonth - fiscalYearStart + 12) % 12;
          return Math.floor(fiscalMonth / 3) + 1;
        };
        if (data_points !== null) {
          const currentQuarter = getCurrentQuarter(
            currentMonth,
            fiscalYearStart,
          );
          lastDp = findDataForCurrentorPreviousQuarter(
            currentQuarter,
            currentYear,
            data_points,
          );
        }
        if (lastDp) {
          currentAverage = Number(lastDp.cumulative_value);
          targetAverage = Number(lastDp.cumulative_target);
        }

        let impactFocusScore = 0;
        if (i.target_direction === "Increase") {
          impactFocusScore = getScore(
            (currentAverage / targetAverage).toFixed(2),
          );
        }
        if (i.target_direction === "Decrease") {
          impactFocusScore = getScore(
            (targetAverage / currentAverage).toFixed(2),
          );
        }

        if (!scoreDetails.has(i.goal)) {
          scoreDetails.set(i.goal, new Map());
        }

        const innerScoreMap = scoreDetails.get(i.goal);
        innerScoreMap.set(i.id, {
          impact_score: impactFocusScore,
          goal_priority: i.goal_priority,
        });
      }

      if (i.tracking_frequency === "Yearly") {
        let lastDp;
        let currentAverage = 0;
        let targetAverage = 0;

        if (data_points !== null) {
          lastDp = findDataForCurrentorPreviousYear(currentYear, data_points);
        }
        if (lastDp) {
          currentAverage = Number(lastDp.cumulative_value);
          targetAverage = Number(lastDp.cumulative_target);
        }
        let impactFocusScore = 0;
        if (i.target_direction === "Increase") {
          impactFocusScore = getScore(
            Number(currentAverage / targetAverage).toFixed(2),
          );
        }

        if (i.target_direction === "Decrease") {
          impactFocusScore = getScore(
            Number((targetAverage / currentAverage).toFixed(2)),
          );
        }

        if (!scoreDetails.has(i.goal)) {
          scoreDetails.set(i.goal, new Map());
        }

        const innerScoreMap = scoreDetails.get(i.goal);
        innerScoreMap.set(i.id, {
          impact_score: impactFocusScore,
          goal_priority: i.goal_priority,
        });
      }
    });
    let weightedAverage = 0;
    for (const [goalId, impact_idMap] of scoreDetails.entries()) {
      const impactsCount = impact_idMap.size;
      let count = 0;
      let index = 0;
      let goalPenalty = 0;
      let lessImpact = false;
      let averageImpactScore = 0;
      for (const [
        impact_id,
        { impact_score, goal_priority },
      ] of impact_idMap.entries()) {
        index++;
        console.log(
          `impactId111: ${impact_id}, impact_score111: ${impact_score}`,
        );
        averageImpactScore = averageImpactScore + impact_score;

        if (impact_score < 8) {
          goalPenalty = (8 - impact_score) * 0.3 + goalPenalty;
          lessImpact = true;
        }

        if (goalPenalty < 1.5 && index === impactsCount && lessImpact) {
          averageImpactScore = averageImpactScore / impactsCount;

          goalFocusScore.set(goalId, {
            goalScore: Math.round(averageImpactScore - goalPenalty),
            goalPriority: goal_priority,
          });
        } else if (goalPenalty > 1.5 && index === impactsCount && lessImpact) {
          averageImpactScore = averageImpactScore / impactsCount;

          goalFocusScore.set(goalId, {
            goalScore: Math.round(averageImpactScore - 1.5),
            goalPriority: goal_priority,
          });
        }

        if (impact_score >= 8) {
          count++;
        }
        if (count === impactsCount) {
          averageImpactScore = averageImpactScore / impactsCount;

          goalFocusScore.set(goalId, {
            goalScore: Math.round(averageImpactScore + 0.5),
            goalPriority: goal_priority,
          });
        }

        if (!goalFocusScore.has(goalId) && index === impactsCount) {
          averageImpactScore = averageImpactScore / impactsCount;
          goalFocusScore.set(goalId, {
            goalScore: Math.round(averageImpactScore),
            goalPriority: goal_priority,
          });
        }
      }
    }
    let priorityWeight;
    let priorityWeightSum = 0;
    const goalsCount = goalFocusScore.size;
    let number,
      penalty = 0;
    let contractPenalty = 0;
    let weightedGoalScore = [];
    for (const [
      goalId,
      { goalScore, goalPriority },
    ] of goalFocusScore.entries()) {
      if (goalScore >= 8) {
        number++;
      }
      console.log(goalId, "goalId");
      if (goalScore < 8) {
        penalty = (8 - goalScore) * 0.3;
        if (goalPriority === "High") {
          penalty = penalty * 1.5;
        }
        if (goalPriority === "Low") {
          penalty = penalty * 0.5;
        }
        if (penalty > 1.5) {
          penalty = 1.5;
        }
        contractPenalty = contractPenalty + penalty;
      }
      weightedGoalScore.push(goalScore);
      if (goalPriority === "Medium") {
        priorityWeight = 2;

        priorityWeightSum = priorityWeightSum + priorityWeight;
      } else if (goalPriority === "High") {
        priorityWeight = 3;
        priorityWeightSum = priorityWeightSum + priorityWeight;
      } else {
        priorityWeight = 1;
        priorityWeightSum = priorityWeightSum + priorityWeight;
      }

      weightedAverage = goalScore * priorityWeight + weightedAverage;
    }
    const mean =
      weightedGoalScore.reduce((sum, n) => sum + n, 0) /
      weightedGoalScore.length;
    const variance =
      weightedGoalScore.reduce((sum, n) => sum + (n - mean) ** 2, 0) /
      weightedGoalScore.length;
    const stdDeviation = Math.sqrt(variance);

    weightedAverage = weightedAverage / priorityWeightSum;
    if (number === goalsCount) {
      weightedAverage = weightedAverage + 0.5;
    }
    if (contractPenalty > 0) {
      weightedAverage = weightedAverage - contractPenalty;
    }
    if (stdDeviation < 0.8) {
      weightedAverage = weightedAverage + 0.3;
    }
    if (stdDeviation > 2) {
      weightedAverage = weightedAverage - 0.3;
    }
    if (weightedAverage > 10) {
      return 10;
    } else {
      return Math.round(weightedAverage);
    }
  };
  if (formType === "Client-Brief") {
    if (isImpactDpByAccountSuccess) {
      focusScore = calculateScoreDataPoints(impactDpByAccountData.data);
    }
    if (isImpactDpByAccountError) {
      // focusScore = 0;
      calculateScoreDataPoints([]);
    }
  }

  return (
    <div>
      <div className='focus-score-circle d-flex align-items-center justify-content-center'>
        <span className='focus-score-value'>{focusScore}</span>
      </div>
      <span className='focus-score-status'>
        <strong>{getScoreStatus(focusScore)}</strong>
      </span>
    </div>
  );
}

export default FocusScoreClient;
